import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box, Autocomplete, TextField, Backdrop, CircularProgress } from "@mui/material";


import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, replace, isUndefined, isNull, cloneDeep } from "lodash";

import itemService from "@plannel/services/data/item-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import AttributeConfigModal from "@plannel/components/modal/AttributeConfigModal";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import DeleteOptionDialog from "@plannel/components/DeleteOptionDialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, SettingsButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import colorUtil from "../utils/color-util";
import codeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import GridDatePicker from "@plannel/components/GridDatePicker";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import ResponseUtil from "../utils/response-util";
import {grey, red} from "@mui/material/colors";

const { itemType, hrchyNum, dpABCConfig } = codeUtil;
const { snackBarMessageColor } = colorUtil;
const { httpStatusCode } = ResponseUtil;
const ItemMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const itemSearch = useRef(reduxViewState?.itemSearch ? reduxViewState.itemSearch : "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const itemTypes = Object.keys(itemType);
  const [itemTypeSearch, setItemTypeSearch] = useState(reduxViewState?.itemTypeSearch ?? null);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [deleteOptionDialogOpen, setDeleteOptionDialogOpen] = useState(false);
  const [shouldCascade, setShouldCascade] = useState(false);
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);
  const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [itemHierarchies, setItemHierarchies] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [openFilter, setOpenFilter] = useState(false);

  const [attrConfigModalOpen, setAttrConfigModalOpen] = useState(false);
  const [attrColDefs, setAttrColDefs] = useState([]);
  const [hrchyColDefs, setHrchyColDefs] = useState([]);
  const userData = JSON.parse(localStorage.getItem("user"));
  const [csvColDef, setCsvColDef] = useState([]);
  const [planConfig, setPlanConfig] = useState({});

  const EXCEL_EXPORT = "excelExport";
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attrFields = ["attr01", "attr02", "attr03", "attr04", "attr05", "attr06", "attr07", "attr08", "attr09", "attr10"];
  const attrColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "attrColsCollapsed") ? reduxViewState.attrColsCollapsed : true;
  const hrchyNumList = {
    itemHrchy: hrchyNum.HRCHY,
    itemHrchy02: hrchyNum.HRCHY02,
    itemHrchy03: hrchyNum.HRCHY03,
    itemHrchy04: hrchyNum.HRCHY04,
    itemHrchy05: hrchyNum.HRCHY05
  }

  const getRowStyle = params => {
    const { delFlg } = params.data;
    if (delFlg) {
      return { background: grey[300] };
    }
    return DataState.getRowStyles(params);
  }

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: codeUtil.module.DP,
      configCd: "ITEM_RATIO_RANGE",
      activeFlg: true,
    };

    planConfigService.getConfigCd(param)
      .then(response => {
        if (response.data) {
          const planConfigData = response.data;
          setPlanConfig(planConfigData);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {}

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (itemTypeSearch) {
      searchFilters.itemType = itemTypeSearch;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveItems = () => {
    const params = getRequestParams();
    itemService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setItems(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, advancedFilters, currentPage, pageSize });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  };

  const splitListByHrchyNum = (list) => {
    const result = {};
    list.forEach(item => {
      const num = item.hrchyNum;
      if (!Object.prototype.hasOwnProperty.call(result, num)) {
        result[num] = [];
      }
      result[num].push(item);
    });
    return result;
  }

  const retrieveItemHierarchies = () => {
    itemHrchyService.getLookup()
      .then(response => {
        const result = response.data.results;
        setItemHierarchies(splitListByHrchyNum(result));
      })
      .catch(e => {
        debug(e);
      });
  }

  const onGridReady = () => {
    retrieveItemHierarchies();
    setGridLoading(true);
  }

  const removeItem = (option = shouldCascade) => {
    const itemIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        itemIds.push(data.id)
      }
    );

    if (itemIds.length > 0) {
      itemService.remove(itemIds, { params: { shouldCascadeDelete: option } })
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveItems();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const checkDisaggregationRatio = () => {
    const error = [];
    if (items === null || items.length === 0) {
      return error;
    }

    const mappedByParent = items.reduce((acc, item) => {
      const key = item.itemHrchy?.hrchyCd;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    for (const hrchyCd of Object.keys(mappedByParent)) {
      const children = mappedByParent[hrchyCd];
      if (children?.length <= 0) {
        continue;
      }
      let totalDisaggRatio = null;
      for (const child of children) {
        if (child.disaggRatio === null) {
          continue;
        }
        totalDisaggRatio = NumberUtil.decimalAdd(totalDisaggRatio, (child.disaggRatio * 100 || 0));
      }
      if (totalDisaggRatio !== null && (totalDisaggRatio > planConfig?.objVal?.max || totalDisaggRatio < planConfig?.objVal?.min)) {
        //error 발생
        const itemCds = children.map(child => child.itemCd);
        error.push(...itemCds);
      }
    }
    return error;
  }

  const saveItem = (option = shouldCascade) => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    
    if (allStateData.length > 0) {
      const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

      const dataWithFlg = allStateData.map((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      let hrchyChanged = false;
      let markedAsDelete = false;
      if (originalData) {
        hrchyChanged = originalData?.itemHrchy?.id !== row?.itemHrchy?.id;
        markedAsDelete = originalData?.delFlg !== row?.delFlg && row?.delFlg === true;
      }
      const hrchyKeys = ["itemHrchy", "itemHrchy02", "itemHrchy03", "itemHrchy04", "itemHrchy05"];
      hrchyKeys.forEach(key => {
        if (row?.[key] === "") {
          row[key] = null;
        }
      });
      
      const shouldCascadeDelete = markedAsDelete ? (option) : false;
      
      return { ...row, hrchyChanged, markedAsDelete, shouldCascadeDelete };
    });

      setProgressOn(true);
      itemService.upsert(dataWithFlg)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { response } = e;
          if (response?.status === httpStatusCode.UnprocessableEntity) {
            const { data } = response;
            const message = data?.message ? data.message : t("MSG_ItemTypeErrorToCustomerItem");
            const errorData = data?.data;

            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(message, { itemCodes: errorData }) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveItems();
        })
        .finally(() => {
          setProgressOn(false);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDeleteOptionConfirm = (option) => {
    setShouldCascade(option);
    if (pendingDeleteAction === "SAVE") {
      saveItem(option);
    } else if (pendingDeleteAction === "DELETE") {
      removeItem(option);
    }
    setPendingDeleteAction(null);
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveItem();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveItems();
    }
  }

  const addRow = () => {
    const newRow = {
      id: null,
      itemCd: null,
      name: null,
      itemType: null,
      uomCd: null,
      descTxt: null,
      itemHrchy: null,
      itemHrchy02: null,
      itemHrchy03: null,
      itemHrchy04: null,
      itemHrchy05: null,
      stdUnitPrice: 0,
      purchaseUnitPrice: 0,
      salesUnitCost: 0,
      invUnitCost: 0,
      shipRelAprvlDt: null,
      readyToSellinDt: null,
      startOfProdDt: null,
      endOfProdDt: null,
      endOfSalesDt: null,
      attr01: null,
      attr02: null,
      attr03: null,
      attr04: null,
      attr05: null,
      attr06: null,
      attr07: null,
      attr08: null,
      attr09: null,
      attr10: null,
      delFlg: false,
      bfFlg: true,
      npiFlg: false,
      priority: 999,
      dpFlg: true,
      invClassCd: null,
      verNum: 0
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const itemValueFormatter = params => {
    const { value } = params;

    return itemType?.[value] ? GridUtils.gridValueL10N(itemType[value]?.name) : value;
  };

  const itemHrchyValueFormatter = (itemHrchy) => {
    return itemHrchy.value ? itemHrchy.value.name : "";
  };

  const itemHrchyKeyCreator = (params) => {
    return params.value.hrchyCd;
  }

  const getItemHrchyList = (num) => {
    if (itemHierarchies === null) {
      return [];
    }
    if (Object.prototype.hasOwnProperty.call(itemHierarchies, num)) {
      return itemHierarchies[num];
    }
    return [];
  };

  const currencyRenderer = (params, isExcel = false) => {
    const { data } = params;
    const symbol = userData?.currencySymbol;
    const value = data?.[params?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (isExcel) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  }
  currencyRenderer.customType = EXCEL_EXPORT;

  const numberSetter = (params) => {
    if (params?.newValue === "" || params?.newValue === null) {
      params.data.disaggRatio = null;
      return true;
    }
    const newValue = parseFloat(params.newValue);
    // 유효성 검사: 숫자이고 음수가 아니어야 함
    if (isNaN(newValue) || newValue < 0) {
      return false; // 값 변경 거부
    }
    if (params.data?.disaggRatio !== newValue) {
      params.data.disaggRatio = newValue;
      return true;
    }
    return false;
  }

  const exceptionCellStyle = params => {
    if (planConfig === null) {
      return {};
    }
    const error = checkDisaggregationRatio();
    if (!isEmpty(error) && error.includes(params.data.itemCd)) {
      return { color: red[700], fontWeight: 'bold' }; // Red background for out of range values
    }
    return {};
  }

  const checkDeleteFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};
    
    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.delFlg !== row.delFlg && row.delFlg === true;
      }
      return false;
    });
  };

  const handleSaveClick = () => {
    gridRef.current?.api?.stopEditing();
    
    const hasDeleteFlgChanges = checkDeleteFlgChanges();
    
    if (hasDeleteFlgChanges) {
      setPendingDeleteAction("SAVE");
      setDeleteOptionDialogOpen(true);
    } else {
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    }
  };


  const columnDefs = [
    {
      headerName: "itemCd",
      field: "itemCd", width: 150,
      cellClass: 'stringType',
      filterType: "string"
    },
    { headerName: "itemName", field: "name", cellClass: 'stringType', width: 150, filterType: "string" },
    {
      headerName: "itemType", field: "itemType", width: 150,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: itemValueFormatter,
      cellEditorParams: {
        valueFormatter: itemValueFormatter,
        values: Object.keys(itemType)
      },
      filterType: "string",
      filterOption: Object.keys(itemType).map(key => key.toLowerCase())
    },
    { headerName: "uomCd", field: "uomCd", width: 70, filterType: "string" },
    { headerName: "descTxt", field: "descTxt", width: 200, filterType: "string" },
    {
      headerName: "invClassCd", field: "invClassCd", minWidth: 60, width: 100,
      cellRenderer: AbcXyzChip,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: {
        values: dpABCConfig
      },
      filterType: "string",
      filterOption: dpABCConfig
    },
    ...hrchyColDefs,
    { headerName: "stdSalesPrice", field: "stdUnitPrice", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100, filterType: "number" },
    { headerName: "purchaseUnitPrice", field: "purchaseUnitPrice", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100, filterType: "number" },
    { headerName: "salesUnitCost", field: "salesUnitCost", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100, filterType: "number" },
    { headerName: "invUnitCost", field: "invUnitCost", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100, filterType: "number" },
    { headerName: "disaggRatio", field: "disaggRatio", valueSetter: (params) => { numberSetter(params); }, type: ["numberColumn", "percentColumn", "rightAligned"], width: 100, cellStyle: exceptionCellStyle, filterType: "percent" },
    { headerName: "shipRelAprvlDt", field: "shipRelAprvlDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: "date", hide: true, valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "readyToSellinDt", field: "readyToSellinDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "startOfProdDt", field: "startOfProdDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "endOfProdDt", field: "endOfProdDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "endOfSalesDt", field: "endOfSalesDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    // {
    //   headerName: "demandPlan", field: "dpFlg", type: ["booleanColumn"], width: 100,
    //   editable: params => {
    //     if (params?.data?.itemType) {
    //       const { itemType } = params.data;
    //       if (params.data.dpFlg === true) {
    //         return true;
    //       }
    //       return isEqual(itemType, "FG") || isEqual(itemType, "KIT"); // dp: FG, KIT
    //     }
    //     return false;
    //   }
    // },
    // {
    //   headerName: "baselineForecast", field: "bfFlg", type: ["booleanColumn"], width: 100,
    //   editable: params => {
    //     if (params?.data?.itemType) {
    //       const { itemType } = params.data;
    //       if (params.data.bfFlg === true) {
    //         return true;
    //       }
    //       return isEqual(itemType, "FG") || isEqual(itemType, "KIT"); //bf : FG, KIT
    //     }
    //     return false;
    //   }
    // },
    { headerName: "newProduct", field: "npiFlg", type: ["booleanColumn"], width: 100, filterType: "boolean" },
    ...attrColDefs,
    { headerName: "priority", field: "priority", type: ["rightAligned"], width: 110, filterType: "number" },
    { headerName: "delFlg", field: "delFlg", type: ["booleanColumn"], width: 100, filterType: "boolean" },
    { headerName: "createdTs", field: "createdTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn", filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn", filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" },
  ];

  const getCsvColDef = () => {
    if (csvColDef.isEmpty) {
      return columnDefs;
    }

    return csvColDef;
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveItems();
    }

    return () => {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams])

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemTypeSearch, advancedFilters])

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters])

  useEffect(() => {
    if (Object.keys(hrchyColDefs).length > 0 && !isEmpty(attrColDefs)) {
      for (const row of hrchyColDefs) {
        row.cellEditorParams = {
          valueFormatter: itemHrchyValueFormatter,
          values: getItemHrchyList(hrchyNumList[row.field])
        }
        row.filterType = "string"
        row.filterOption = getItemHrchyList(hrchyNumList[row.field]).map(item => item.name);
      }

      for (const row of attrColDefs) {
        row.filterType = "string"
        row.filterName = row.field;
      }

      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
      setCsvColDef(cloneDeep(columnDefs))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hrchyColDefs, attrColDefs, itemHierarchies]);

  useEffect(() => {
    if (items) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planConfig, items]);

  useEffect(() => {
    retrievePlanConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef}/>
        <Autocomplete
          options={itemTypes}
          getOptionLabel={option => itemType ? t(itemType[option]?.name) : option}
          value={itemTypeSearch}
          onChange={(_, newValue) => setItemTypeSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("itemType")} />
          )}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={itemService} params={{ ...getRequestParams(false) }} columnDefs={getCsvColDef()} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => {
            gridRef.current?.api?.stopEditing();
            setPendingDeleteAction("DELETE");
            setDeleteOptionDialogOpen(true);
          }} />
          <SaveButton onClick={handleSaveClick} />
          <SettingsButton label="settings" onClick={() => setAttrConfigModalOpen(true)} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="item-master-grid" className="ag-theme-balham grid-area" sx={{ position: 'relative', height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column', mb: 2 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={items}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              getRowStyle={getRowStyle}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <DeleteOptionDialog
        open={deleteOptionDialogOpen}
        onClose={() => {
          setDeleteOptionDialogOpen(false);
          setPendingDeleteAction(null);
        }}
        onConfirm={handleDeleteOptionConfirm}
        title="MSG_DeleteOption"
        content={`${t("MSG_AssociatedDataAffected")}\n\n${codeUtil.makeMessage(codeUtil.deleteRelations.item, 'delete', true)}`}
        t={t}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <AttributeConfigModal
        viewName={viewName}
        open={attrConfigModalOpen}
        setOpen={setAttrConfigModalOpen}
        target={"item"}
        attrFields={attrFields}
        hrchyColDefs={hrchyColDefs}
        hrchyColumnOptions={{
          type: ["defaultValueParser"],
          keyCreator: itemHrchyKeyCreator
        }}
        hrchyNumList={hrchyNumList}
        setAttrColDefs={setAttrColDefs}
        attrColumnOptions={{ valueParser: (params) => String(params.newValue) }}
        setHrchyColDefs={setHrchyColDefs}
        attrColsCollapsed={attrColsCollapsed}
        setPlanConfig={setPlanConfig}
        planConfig={planConfig}
        hasRatioRange
      />
    </>
  );
};

export default withTranslation()(ItemMaster);
